import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";

import { Box, Checkbox, Typography } from "@mui/material";
import { EventBusy } from "@mui/icons-material";

import { EventsTrainSortColumn } from "@framework/types";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useApiCall } from "@framework/react-hooks";
import { ConfirmationDialog } from "@framework/mui-dialog-confirmation";
import { toFakeUtc } from "@framework/provider-asu";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { DataExportButton } from "../../../../../../components/common/buttons/export-button";
import { downloadCsv } from "../../../../../../utils/download-csv";
import { SOUND_ALARM_REFRESH_EVENT } from "../../../../../../components/common/header/sound-alarm/constants";
import { useEventType } from "../../../../../../utils/event";
import { mapEventsToRows } from "./mappers";
import { useFormatDateHumanReadable, useToFakeUtcISO } from "../../../../../../hooks";
import { useTrainTables } from "../hooks/use-train-tables";
import { RECORDER_TAKE } from "./constants";
import { IResponseEventsLog, IEventsPageResponse, IRequestEventsLog } from "./interfaces";
import { TrainEventsTable } from "./table";

const HEADER_PREFIX = "pages.eventsLog.messages.headers";

export const TrainEventsSchemeTable: FC<{ embedded?: boolean; isActiveTableTab?: boolean }> = props => {
  const { embedded, isActiveTableTab } = props;

  const [rawEvents, setRawEvents] = useState<IResponseEventsLog[]>([]);
  const [loadServiceEvents, setLoadServiceEvents] = useState<boolean>(false);
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [ackEventId, setAckEventId] = useState<number | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(embedded ? 5 : 25);
  const [totalCount, setTotalCount] = useState(0);

  const rawEventsLengthRef = useRef(0);
  rawEventsLengthRef.current = rawEvents.length;

  const recorderFetchGenRef = useRef(0);
  const isResettingRef = useRef(false);

  const {
    selectedItem,
    trainId,
    startTimestamp,
    endTimestamp,
    dbTz,
    isRecorderMode,
    recorderWindowStart,
    recorderTickId,
    sortColumn,
    sortOrder,
    handleSort,
    canFetch,
  } = useTrainTables({
    isActiveTableTab,
    defaultSortColumn: EventsTrainSortColumn.EventLogDate,
    onReset: () => {
      if (isRecorderMode && recorderTickId !== null && recorderTickId > 1) return;
      isResettingRef.current = true;
      setRawEvents([]);
      setCurrentPage(0);
    },
    onSort: () => {
      setCurrentPage(0);
    },
  });

  const serverTake = isRecorderMode ? RECORDER_TAKE : rowsPerPage;
  const skip = isRecorderMode ? 0 : currentPage * serverTake;

  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const toFakeUtcISO = useToFakeUtcISO();

  const noEvents = formatMessage({ id: "pages.scheme.events.noEvents" });
  const date = toFakeUtcISO(new Date()).split("T")[0];
  const exportFileTitle =
    (selectedItem && "TrainName" in selectedItem && selectedItem.TrainName) ||
    formatMessage({ id: "pages.eventsLog.title" });
  const exportFileName = `train-events-report-${exportFileTitle}-${date}`;

  const { getEventType, getMessage } = useEventType();

  const recorderWindowStartRef = useRef(recorderWindowStart);
  recorderWindowStartRef.current = recorderWindowStart;

  const endTimestampRef = useRef(endTimestamp);
  endTimestampRef.current = endTimestamp;

  const dbTzRef = useRef(dbTz);
  dbTzRef.current = dbTz;

  // refs justLeftRecorderModeRef и prevIsRecorderModeRef для того, чтобы не запускать эффект при первом рендере после выхода из recorder mode.
  const justLeftRecorderModeRef = useRef(false);
  const prevIsRecorderModeRef = useRef(isRecorderMode);

  if (prevIsRecorderModeRef.current !== isRecorderMode) {
    prevIsRecorderModeRef.current = isRecorderMode;
    if (!isRecorderMode) {
      justLeftRecorderModeRef.current = true;
    }
  }

  const recorderTrimLeftMs = (): number | null => {
    const wStr = recorderWindowStartRef.current;
    if (!wStr) return null;
    const w = new Date(wStr).getTime();
    return Number.isNaN(w) ? null : w;
  };

  const trimRawEventsToWindow = useCallback((rows: IResponseEventsLog[]): IResponseEventsLog[] => {
    const leftMs = recorderTrimLeftMs();
    if (leftMs === null) return rows;
    const endMs = new Date(endTimestampRef.current).getTime();
    if (!Number.isNaN(endMs) && leftMs >= endMs) {
      return rows;
    }
    const tz = dbTzRef.current;
    return rows.filter(e => {
      const t = toFakeUtc(e.EventLogDate, tz).getTime();
      return !Number.isNaN(t) && t >= leftMs;
    });
  }, []);

  const mergeUniqueEvents = (prev: IResponseEventsLog[], next: IResponseEventsLog[]): IResponseEventsLog[] => {
    const existingIds = new Set(prev.map(e => e.idEvent));
    const newItems = next.filter(item => !existingIds.has(item.idEvent));
    return [...prev, ...newItems];
  };

  const recorderRawForDisplay = useMemo((): IResponseEventsLog[] => {
    if (!isRecorderMode) return [];
    if (recorderTickId === 1) return rawEvents;
    return trimRawEventsToWindow(rawEvents);
  }, [isRecorderMode, rawEvents, recorderTickId]);

  const tableRows = useMemo(
    () =>
      mapEventsToRows(isRecorderMode ? recorderRawForDisplay : rawEvents, {
        formatDate,
        formatMessage,
        getEventType,
        getMessage,
      }),
    [isRecorderMode, recorderRawForDisplay, rawEvents, formatDate, formatMessage, getEventType, getMessage],
  );

  const rawEventsMap = useMemo(() => new Map(rawEvents.map(e => [e.idEvent, e])), [rawEvents]);

  const { fn: getEventLog, isLoading: isGettingEventLog } = useApiCall<IEventsPageResponse>(
    (api, { id, loadServiceEvents, skip, sortOrder, sortColumn, take, startOverride }: IRequestEventsLog) => {
      const requestSortColumn = isRecorderMode ? EventsTrainSortColumn.EventLogDate : sortColumn;
      const requestSortOrder = isRecorderMode ? "desc" : sortOrder;
      const data = {
        startTimestamp: startOverride ?? startTimestamp,
        endTimestamp,
        idTrain: id,
        loadServiceEvents,
        skip,
        take,
        ...(requestSortColumn ? { sortColumn: requestSortColumn, sortOrder: requestSortOrder } : {}),
      };

      // for Station events we use: services/ui/src/pages/dashboard/tabs/events-log/table.tsx
      return api.fetchJson({
        url: `/events/train`,
        data,
      }) as Promise<IEventsPageResponse>;
    },
    { success: false },
  );

  const { fn: setAckStatus, isLoading: isAcknowledging } = useApiCall(
    (api, values: { ackEventId: number }) => {
      const { ackEventId } = values;
      return (
        api.fetchJson({
          url: `/events/ack-event/${ackEventId}`,
          method: "PUT",
        }) as Promise<unknown>
      )
        .then(() => {
          window.dispatchEvent(new Event(SOUND_ALARM_REFRESH_EVENT));

          return getEventLog(undefined, {
            id: trainId,
            loadServiceEvents,
            skip,
            take: serverTake,
          });
        })
        .then((json: unknown) => {
          const page = json as IEventsPageResponse | null | undefined;
          const rows = page?.items;
          if (!rows?.length) return;
          setRawEvents(rows);
        })
        .catch(error => {
          console.error("Error acknowledging event:", error);
          throw error;
        })
        .finally(() => {
          setAckDialogOpen(false);
          setAckEventId(null);
        });
    },
    { success: false },
  );

  const handleAckClick = useCallback((idEvent: number) => {
    setAckEventId(idEvent);
    setAckDialogOpen(true);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  }, []);

  const handleExport = useCallback((): void => {
    if (!tableRows.length) return;

    downloadCsv(
      tableRows.map(row => ({
        ackStatus: row.ackStatus != null ? formatMessage({ id: `enums.ackStatus.${row.ackStatus}` }) : "",
        status: row.status ?? "",
        type: row.type ?? "",
        pointName: row.pointName ?? "",
        date: row.date ?? "",
        parameter: row.parameter ?? "",
        source: row.source ?? "",
        fullName: row.fullName ?? "",
        message: row.message ?? "",
        defect: row.defect ?? "",
      })),
      `${exportFileName}.csv`,
      [
        { field: "ackStatus", title: formatMessage({ id: `${HEADER_PREFIX}.ackStatus` }) },
        { field: "status", title: formatMessage({ id: `${HEADER_PREFIX}.status` }) },
        { field: "type", title: formatMessage({ id: `${HEADER_PREFIX}.eventType` }) },
        { field: "pointName", title: formatMessage({ id: `${HEADER_PREFIX}.point` }) },
        { field: "date", title: formatMessage({ id: `${HEADER_PREFIX}.dateTime` }) },
        { field: "parameter", title: formatMessage({ id: `${HEADER_PREFIX}.parameter` }) },
        { field: "source", title: formatMessage({ id: `${HEADER_PREFIX}.source` }) },
        { field: "fullName", title: formatMessage({ id: `${HEADER_PREFIX}.fullName` }) },
        { field: "message", title: formatMessage({ id: `${HEADER_PREFIX}.message` }) },
        { field: "defect", title: formatMessage({ id: `${HEADER_PREFIX}.defect` }) },
      ],
    );
  }, [tableRows, exportFileName, formatMessage]);

  useEffect(() => {
    if (!canFetch) return;
    if (isRecorderMode) return;
    if (isResettingRef.current && currentPage !== 0) return;

    isResettingRef.current = false;

    if (justLeftRecorderModeRef.current) {
      justLeftRecorderModeRef.current = false;
      return;
    }

    void getEventLog(undefined, {
      id: trainId,
      loadServiceEvents,
      skip: currentPage * serverTake,
      take: serverTake,
      sortColumn,
      sortOrder,
    }).then(json => {
      const response = json as IEventsPageResponse;

      setRawEvents(response.items ?? []);
      setTotalCount(response.totalCount ?? 0);
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    loadServiceEvents,
    currentPage,
    serverTake,
    sortColumn,
    sortOrder,
    isActiveTableTab,
  ]);

  useEffect(() => {
    if (!canFetch) return;
    if (!isRecorderMode) return;

    const fetchGen = ++recorderFetchGenRef.current;

    const useFullRecorderWindow = recorderTickId === 1 || rawEventsLengthRef.current === 0;
    const startOverride = useFullRecorderWindow ? recorderWindowStart ?? startTimestamp : undefined;

    void getEventLog(undefined, {
      id: trainId,
      loadServiceEvents,
      skip,
      take: serverTake,
      ...(startOverride !== undefined ? { startOverride } : {}),
    }).then((json: unknown) => {
      if (fetchGen !== recorderFetchGenRef.current) return;
      const page = json as IEventsPageResponse | null | undefined;
      const data = page?.items ?? [];

      if (data.length === 0) {
        if (recorderTickId === 1) {
          setRawEvents([]);
        }
        return;
      }

      if (recorderTickId === 1) {
        setRawEvents(data);
      } else {
        setRawEvents(prev => mergeUniqueEvents(prev, data));
      }
    });
  }, [
    trainId,
    startTimestamp,
    endTimestamp,
    loadServiceEvents,
    recorderWindowStart,
    isActiveTableTab,
    isRecorderMode,
    recorderTickId,
  ]);

  const loading = isGettingEventLog || isAcknowledging;

  const toolbar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "34px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          size="small"
          checked={loadServiceEvents}
          onChange={e => setLoadServiceEvents(e.currentTarget.checked)}
        />
        <Typography sx={{ fontSize: "12px" }}>{formatMessage({ id: "pages.eventsLog.serviceEvents" })}</Typography>
      </Box>

      <DataExportButton handleExport={handleExport} />
    </Box>
  );

  return (
    <>
      <ConfirmationDialog
        open={ackDialogOpen}
        onCancel={() => setAckDialogOpen(false)}
        onConfirm={() => {
          if (ackEventId) {
            void setAckStatus(undefined, { ackEventId });
          }
        }}
        maxWidth="xs"
        disabled={isAcknowledging}
        message="pages.eventsLog.ack.confirmTitle"
      >
        <ProgressOverlay isLoading={isAcknowledging}>
          <FormattedMessage id="pages.eventsLog.ack.confirmText" />
        </ProgressOverlay>
      </ConfirmationDialog>

      <ProgressOverlay
        isLoading={loading}
        wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
          {tableRows.length ? (
            <>
              {toolbar}
              <TrainEventsTable
                rows={tableRows}
                rawEventsMap={rawEventsMap}
                handleSort={handleSort}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                embedded={embedded}
                onAckClick={handleAckClick}
                totalCount={isRecorderMode ? tableRows.length : totalCount}
                page={currentPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                isServerPagination={!isRecorderMode}
              />
            </>
          ) : (
            <EmptyPlaceholder icon={<EventBusy />} text={noEvents} />
          )}
        </Box>
      </ProgressOverlay>
    </>
  );
};
